import React, { useState, useEffect } from 'react';
import "../styles/Staking.css";
import { ethers } from 'ethers';
import { Pie, Line, Bar, Bubble } from 'react-chartjs-2';
import useWallet from '../hooks/useWallet';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title
} from 'chart.js';
import { Card, Table, Button, Form, Row, Col, ProgressBar, Badge } from 'react-bootstrap';
import { formatEther } from 'ethers/lib/utils';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title
);

// Contract Addresses
const stakingAddress = "0x987E30aaa8Ea37ABC85B1309176A05519E454Fc6"; // ✅ V3 Staking Contract
const saleTokenAddress = "0x62217F8c102A33b6112C8B5B897BB9ee400737a5"; // ✅ MediCoin Token (MCN)

// ABIs
const stakingAbi = [
  "function plans(uint256) view returns (uint256 lockPeriod, uint256 apy, uint256 penaltyPercent, bool allowEarlyWithdraw, bool compoundMonthly)",
  "function stake(uint8 planId, uint256 amount)",
  "function getUserStakes(address user) view returns (tuple(uint256 amount, uint256 startTime, uint8 planId, bool active)[])",
  "function claim(uint256 stakeId)",
  "function earlyWithdraw(uint256 stakeId)",
  "function calculateRewards(uint256 principal, uint256 apyBP, uint256 durationInSeconds, bool isCompoundMonthly) pure returns (uint256)",
  "function balanceOf(address account) view returns (uint256)",
  "function getContractBalance() view returns (uint256)",
  "function owner() view returns (address)",
  "function totalStakedPerPlan(uint8) view returns (uint256)",
  "function totalStakersPerPlan(uint8) view returns (uint256)",
  "function capsEnabled() view returns (bool)",
  "function globalCapEnabled() view returns (bool)",
  "function getTotalGlobalStaked() view returns (uint256)",
  "function getUserTotalStaked(address) view returns (uint256)"
];

const tokenAbi = [
  "function balanceOf(address account) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

const Staking = () => {
  const { wallet, signer, provider, connectWallet, wcProvider } = useWallet();
  const [stakingContract, setStakingContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [tokenBalance, setTokenBalance] = useState("0");
  const [contractBalance, setContractBalance] = useState("0");
  const [isAdmin, setIsAdmin] = useState(false);
  const [stakingPlans, setStakingPlans] = useState([]);
  const [userStakes, setUserStakes] = useState([]);
  const [globalTotals, setGlobalTotals] = useState([]);
  const [globalCounts, setGlobalCounts] = useState([]);
  const [calculatorAmount, setCalculatorAmount] = useState(100000);
  const [calculatorPlan, setCalculatorPlan] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices and tablets
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Tablet|Mobile/i.test(userAgent) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      setIsMobile(isMobileOrTablet);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get the correct provider based on connection type
  const getProvider = () => {
    if (wcProvider) {
      return new ethers.providers.Web3Provider(wcProvider);
    } else if (provider) {
      return provider;
    } else if (window.ethereum) {
      return new ethers.providers.Web3Provider(window.ethereum);
    } else {
      throw new Error("No provider available");
    }
  };

  // Handle wallet connection with mobile-optimized UI (numbered prompt)
  const handleConnectWallet = async () => {
    try {
      console.log("🔍 Connect wallet triggered");
      console.log("Is mobile/tablet:", isMobile);
      console.log("Has ethereum:", !!window.ethereum);
      
      if (isMobile && !window.ethereum) {
        console.log("📱 Mobile/Tablet detected without wallet, showing selection");
        
        const walletOptions = [
          "1️⃣ MetaMask",
          "2️⃣ Trust Wallet", 
          "3️⃣ Rainbow Wallet",
          "4️⃣ Coinbase Wallet",
          "5️⃣ WalletConnect (QR Code)"
        ].join('\n');
        
        const walletChoice = prompt(
          `Select your wallet:\n\n${walletOptions}\n\nEnter number (1-5):`
        );
        
        console.log("User selected:", walletChoice);
        
        switch(walletChoice?.trim()) {
          case '1':
            window.location.href = `https://metamask.app.link/dapp/${window.location.hostname}${window.location.pathname}`;
            break;
          case '2':
            window.location.href = `trust://open_url?coin_id=60&url=${window.location.href}`;
            break;
          case '3':
            window.location.href = `rainbow://open?url=${window.location.href}`;
            break;
          case '4':
            window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`;
            break;
          case '5':
          default:
            console.log("🔄 Initiating WalletConnect");
            const existingModals = document.querySelectorAll('wcm-modal, w3m-modal');
            existingModals.forEach(modal => modal.remove());
            
            await connectWallet();
            
            setTimeout(() => {
              const modals = document.querySelectorAll('wcm-modal, w3m-modal');
              modals.forEach(modal => {
                modal.style.display = 'none';
                modal.remove();
              });
            }, 2000);
            break;
        }
      } else {
        console.log("💻 Standard connection flow");
        await connectWallet();
        
        setTimeout(() => {
          const modals = document.querySelectorAll('wcm-modal, w3m-modal');
          modals.forEach(modal => {
            modal.style.display = 'none';
          });
        }, 2000);
      }
    } catch (error) {
      console.error("Connection error:", error);
      alert("Failed to connect wallet. Please try again.");
    }
  };

  // Initialize Contracts
  useEffect(() => {
    if (signer && wallet) {
      const staking = new ethers.Contract(stakingAddress, stakingAbi, signer);
      const token = new ethers.Contract(saleTokenAddress, tokenAbi, signer);
      setStakingContract(staking);
      setTokenContract(token);
    }
  }, [signer, wallet]);

  // Load Staking Data
  useEffect(() => {
    const loadData = async () => {
      if (stakingContract && tokenContract && wallet && provider) {
        setLoading(true);
        try {
          const plans = [];
          const globalStakedAmounts = [];
          const globalStakerCounts = [];
          const planCount = 5;
          
          for (let i = 0; i < planCount; i++) {
            const plan = await stakingContract.plans(i);
            plans.push({
              id: i,
              durationDays: Math.floor(plan.lockPeriod.toNumber() / (60 * 60 * 24)),
              apy: plan.apy,
              penaltyPercent: plan.penaltyPercent,
              allowEarlyWithdraw: plan.allowEarlyWithdraw,
              compoundMonthly: plan.compoundMonthly,
            });
            
            // Fetch global data for ALL users
            const totalStaked = await stakingContract.totalStakedPerPlan(i);
            const totalStakers = await stakingContract.totalStakersPerPlan(i);
            globalStakedAmounts.push(parseFloat(formatEther(totalStaked)));
            globalStakerCounts.push(totalStakers.toNumber());
          }
          
          setStakingPlans(plans);
          setGlobalTotals(globalStakedAmounts);
          setGlobalCounts(globalStakerCounts);

          const stakes = await stakingContract.getUserStakes(wallet);
          setUserStakes(stakes.map((stake, index) => ({
            id: index,
            amount: stake.amount,
            startTime: stake.startTime.toNumber(),
            planId: stake.planId,
            active: stake.active,
          })));

          const balance = await tokenContract.balanceOf(wallet);
          setTokenBalance(formatEther(balance));

          // Check if connected wallet is the contract owner
          try {
            const ownerAddress = await stakingContract.owner();
            const isOwner = ownerAddress.toLowerCase() === wallet.toLowerCase();
            setIsAdmin(isOwner);

            // Only load contract balance if user is admin
            if (isOwner) {
              const contractBal = await stakingContract.getContractBalance();
              setContractBalance(formatEther(contractBal));
            }
          } catch (error) {
            console.error('Error checking admin status:', error);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error loading staking data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
  }, [stakingContract, tokenContract, wallet, provider]);

  const formatDuration = (days) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      const remainingDays = days % 365;
      return `${years} Year${years > 1 ? 's' : ''}${remainingDays > 0 ? ` ${remainingDays} Days` : ''}`;
    }
    return `${days} Days`;
  };

  const aggregateStats = () => {
    const totals = Array(stakingPlans.length).fill(0);
    const count = Array(stakingPlans.length).fill(0);
    userStakes.forEach(stake => {
      totals[stake.planId] += parseFloat(formatEther(stake.amount));
      count[stake.planId] += 1;
    });
    return { totals, count };
  };

  const handleStakeClick = async (planIndex) => {
    if (!wallet) {
      await connectWallet();
      return;
    }
    setCalculatorPlan(planIndex);
    setCalculatorAmount(100000);
    window.scrollTo({
      top: document.getElementById('staking-calculator')?.offsetTop || 0,
      behavior: 'smooth'
    });
  };

  const handleStake = async () => {
    if (!stakingContract || !tokenContract || !wallet) {
      await connectWallet();
      alert("Please connect wallet first!");
      return;
    }

    if (calculatorAmount <= 0) {
      alert("Please enter a valid amount to stake.");
      return;
    }

    try {
      setLoading(true);

      const _provider = getProvider();
      const _signer = _provider.getSigner();

      // Network check with retry for mobile
      let network;
      let retries = 3;
      while (retries > 0) {
        try {
          network = await _provider.getNetwork();
          break;
        } catch (error) {
          retries--;
          if (retries === 0) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (network.chainId !== 1) {
        alert("⚠️ Please switch your wallet to Ethereum Mainnet.");
        setLoading(false);
        return;
      }

      const _stakingContract = new ethers.Contract(stakingAddress, stakingAbi, _signer);
      const _tokenContract = new ethers.Contract(saleTokenAddress, tokenAbi, _signer);

      const amountInWei = ethers.utils.parseEther(calculatorAmount.toString());
      const allowance = await _tokenContract.allowance(wallet, stakingAddress);

      if (allowance.lt(amountInWei)) {
        const approveTx = await _tokenContract.approve(stakingAddress, amountInWei);
        await approveTx.wait();
      }

      const stakeTx = await _stakingContract.stake(calculatorPlan, amountInWei);
      await stakeTx.wait();

      alert("✅ Staking successful!");

      // Reload updated data
      const plans = [];
      const planCount = 5;
      for (let i = 0; i < planCount; i++) {
        const plan = await _stakingContract.plans(i);
        plans.push({
          id: i,
          durationDays: Math.floor(plan.lockPeriod.toNumber() / (60 * 60 * 24)),
          apy: plan.apy,
          penaltyPercent: plan.penaltyPercent,
          allowEarlyWithdraw: plan.allowEarlyWithdraw,
          compoundMonthly: plan.compoundMonthly,
        });
      }
      setStakingPlans(plans);

      const stakes = await _stakingContract.getUserStakes(wallet);
      setUserStakes(stakes.map((stake, index) => ({
        id: index,
        amount: stake.amount,
        startTime: stake.startTime.toNumber(),
        planId: stake.planId,
        active: stake.active,
      })));

      const balance = await _tokenContract.balanceOf(wallet);
      setTokenBalance(formatEther(balance));
      
      // Clean up modals for mobile
      setTimeout(() => {
        const modals = document.querySelectorAll('wcm-modal, w3m-modal');
        modals.forEach(modal => {
          modal.style.display = 'none';
        });
      }, 1000);
    } catch (err) {
      console.error("Staking failed", err);
      alert("❌ Staking failed: " + (err.reason || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (stakeId, isEarly) => {
    if (!stakingContract || !wallet) return;

    try {
      setLoading(true);
      
      const _provider = getProvider();
      const _signer = _provider.getSigner();

      // Network check with retry for mobile
      let network;
      let retries = 3;
      while (retries > 0) {
        try {
          network = await _provider.getNetwork();
          break;
        } catch (error) {
          retries--;
          if (retries === 0) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (network.chainId !== 1) {
        alert("⚠️ Please switch your wallet to Ethereum Mainnet.");
        setLoading(false);
        return;
      }

      const _stakingContract = new ethers.Contract(stakingAddress, stakingAbi, _signer);
      const _tokenContract = new ethers.Contract(saleTokenAddress, tokenAbi, _signer);

      const tx = isEarly
        ? await _stakingContract.earlyWithdraw(stakeId)
        : await _stakingContract.claim(stakeId);

      await tx.wait();
      alert("✅ Withdrawal successful!");

      // Reload updated data
      const plans = [];
      const planCount = 5;
      for (let i = 0; i < planCount; i++) {
        const plan = await _stakingContract.plans(i);
        plans.push({
          id: i,
          durationDays: Math.floor(plan.lockPeriod.toNumber() / (60 * 60 * 24)),
          apy: plan.apy,
          penaltyPercent: plan.penaltyPercent,
          allowEarlyWithdraw: plan.allowEarlyWithdraw,
          compoundMonthly: plan.compoundMonthly,
        });
      }
      setStakingPlans(plans);

      const stakes = await _stakingContract.getUserStakes(wallet);
      setUserStakes(stakes.map((stake, index) => ({
        id: index,
        amount: stake.amount,
        startTime: stake.startTime.toNumber(),
        planId: stake.planId,
        active: stake.active,
      })));

      const balance = await _tokenContract.balanceOf(wallet);
      setTokenBalance(formatEther(balance));
      
      // Clean up modals for mobile
      setTimeout(() => {
        const modals = document.querySelectorAll('wcm-modal, w3m-modal');
        modals.forEach(modal => {
          modal.style.display = 'none';
        });
      }, 1000);
    } catch (error) {
      console.error("Withdraw failed:", error);
      alert("❌ Withdrawal failed. " + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  // User's personal stats (for "Individual Staking Analytics" section)
  const { totals, count } = aggregateStats();

  // Global stats for "Global Staking Analytics" section
  const maxGlobalStakers = Math.max(...globalCounts, 1);
  const isThreeYearPopular = globalCounts[3] >= maxGlobalStakers * 0.8;
  const bubbleData = stakingPlans.map((plan, i) => {
    const baseRadius = Math.max(5, Math.min(30, globalCounts[i] * 3));
    const radius = i === 3 && isThreeYearPopular ? baseRadius * 1.5 : baseRadius;
    return {
      x: i + 1,
      y: globalCounts[i],
      r: radius,
    };
  });

  return (
    <div className="staking-dashboard">
      {loading && (
        <div className="staking-loading">
          <div className="spinner"></div>
        </div>
      )}

      <h1 className="staking-title">🛡 MediCoin Staking Dashboard</h1>
      <p className="staking-intro">
        MediCoin's staking program is designed to reward long-term commitment with attractive, compounding yields across multiple lock-in periods. By staking MCN tokens, holders contribute to ecosystem stability while earning predictable returns aligned with our long-term growth model.
      </p>
      {wallet ? (
        <div className="wallet-status-banner">
          <p>
            <strong>🔗 Connected:</strong> {wallet.slice(0, 6)}...{wallet.slice(-4)}  | 
            <strong>💰 Your Balance:</strong> {parseFloat(tokenBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} MCN
            {isAdmin && (
              <>
                {' | '}
                <strong>📊 Contract Balance:</strong> {parseFloat(contractBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} MCN
                {' '}
                <Badge bg="danger" style={{ fontSize: '0.7em', verticalAlign: 'middle' }}>ADMIN</Badge>
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="wallet-connect-banner">
          <p>🔐 Please connect your wallet to stake and view your dashboard.</p>
          <Button className="connect-wallet-btn" onClick={handleConnectWallet}>
            Connect Wallet
          </Button>
        </div>
      )}

      {/* Available Staking Plans Table */}
      <div className="staking-section">
        <h2 className="staking-section-title">Available Staking Plans</h2>
        <div className="staking-table-wrapper">
          <Table responsive hover className="staking-table">
            <thead>
              <tr>
                <th>Duration</th>
                <th>APY</th>
                <th>Compounding</th>
                <th>Early Withdrawal</th>
                <th>Penalty</th>
                <th>Final Amount (100,000 MCN)</th>
                <th>Net Gain</th>
                <th>ROI %</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {stakingPlans.map((plan, index) => {
                const exampleAmount = 100000;
                let finalAmount;
                
                // ✅ Properly check compoundMonthly flag
                if (plan.compoundMonthly) {
                  // Monthly compounding formula
                  const months = plan.durationDays / 30;
                  const monthlyRate = (plan.apy / 10000) / 12;
                  finalAmount = exampleAmount * Math.pow((1 + monthlyRate), months);
                } else {
                  // Simple interest formula
                  const years = plan.durationDays / 365;
                  const reward = (exampleAmount * plan.apy * years) / 10000;
                  finalAmount = exampleAmount + reward;
                }
                
                const reward = finalAmount - exampleAmount;

                return (
                  <tr key={index}>
                    <td>{formatDuration(plan.durationDays)}</td>
                    <td>{(plan.apy / 100).toFixed(2)}%</td>
                    <td>{plan.compoundMonthly ? 'Monthly' : 'Simple'}</td>
                    <td>{plan.allowEarlyWithdraw ? 'Yes' : 'No'}</td>
                    <td>{(plan.penaltyPercent / 100).toFixed(2)}%</td>
                    <td>{finalAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td>+{reward.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td>{(reward / exampleAmount * 100).toFixed(2)}%</td>
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        className="staking-action-btn"
                        disabled={!wallet}
                        onClick={() => handleStakeClick(index)}
                      >
                        Stake
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Profit Calculator */}
      <div className="staking-section">
        <h2 className="staking-section-title">Profit Calculator</h2>
        <div id="staking-calculator"></div>
        <Row className="staking-calculator-wrapper">
          <Col md={6}>
            <Card className="staking-calculator-card">
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Select Staking Plan</Form.Label>
                    <Form.Select
                      value={calculatorPlan}
                      onChange={(e) => setCalculatorPlan(parseInt(e.target.value))}
                    >
                      {stakingPlans.length > 0 ? (
                        stakingPlans.map((plan, index) => (
                          <option key={index} value={index}>
                            {formatDuration(plan.durationDays)} - {(plan.apy / 100).toFixed(2)}% APY
                          </option>
                        ))
                      ) : (
                        <option>Loading Plans...</option>
                      )}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Enter Token Amount (MCN)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="e.g. 100000"
                      value={calculatorAmount}
                      onChange={(e) => setCalculatorAmount(parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                  <Button
                    variant="success"
                    size="lg"
                    className="staking-confirm-btn w-100"
                    disabled={!calculatorAmount || !wallet}
                    onClick={handleStake}
                    style={{
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      padding: '12px 24px',
                      marginTop: '10px',
                      boxShadow: !calculatorAmount || !wallet ? 'none' : '0 4px 15px rgba(40, 167, 69, 0.4)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🚀 Confirm Stake
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            {stakingPlans[calculatorPlan] && (
              <Card className="staking-calculator-results">
                <Card.Body>
                  <h5 className="text-center mb-3">Projected Earnings</h5>
                  <div className="staking-calculator-info">
                    {(() => {
                      const plan = stakingPlans[calculatorPlan];
                      let finalAmount;
                      
                      // ✅ Properly check compoundMonthly flag
                      if (plan.compoundMonthly) {
                        // Monthly compounding
                        const months = plan.durationDays / 30;
                        const monthlyRate = (plan.apy / 10000) / 12;
                        finalAmount = calculatorAmount * Math.pow((1 + monthlyRate), months);
                      } else {
                        // Simple interest
                        const years = plan.durationDays / 365;
                        const reward = (calculatorAmount * plan.apy * years) / 10000;
                        finalAmount = calculatorAmount + reward;
                      }
                      
                      const netGain = finalAmount - calculatorAmount;
                      const roi = (netGain / calculatorAmount) * 100;
                      
                      return (
                        <>
                          <p>
                            <strong>Final Amount:</strong>{' '}
                            {finalAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} MCN
                          </p>
                          <p>
                            <strong>Net Gain:</strong>{' '}
                            {netGain.toLocaleString(undefined, { maximumFractionDigits: 2 })} MCN
                          </p>
                          <p>
                            <strong>ROI:</strong>{' '}
                            {roi.toFixed(2)}%
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  <div className="staking-calculator-note">
                    {stakingPlans[calculatorPlan]?.compoundMonthly ? (
                      <small>Calculated with <strong>Monthly Compounding</strong></small>
                    ) : (
                      <small>Calculated with <strong>Simple Interest</strong></small>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </div>

      {/* Global Staking Analytics */}
      <div className="staking-section">
        <h2 className="staking-section-title">📊 Global Staking Analytics</h2>
        <div className="staking-charts-grid">
          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">Plan Popularity (Bubble Chart)</h5>
            <Bubble
              data={{
                datasets: [
                  {
                    label: 'Plan Popularity',
                    data: bubbleData,
                    backgroundColor: bubbleData.map((data, i) =>
                      i === 3 && isThreeYearPopular
                        ? 'rgba(255, 99, 132, 0.8)'
                        : 'rgba(75, 192, 192, 0.6)'
                    ),
                    borderColor: bubbleData.map((data, i) =>
                      i === 3 && isThreeYearPopular
                        ? 'rgba(255, 99, 132, 1)'
                        : 'rgba(75, 192, 192, 1)'
                    ),
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${formatDuration(stakingPlans[ctx.dataIndex].durationDays)}: ${ctx.raw.y} stakers`,
                    },
                  },
                },
                scales: {
                  x: { title: { display: true, text: 'Plan Index' } },
                  y: { title: { display: true, text: 'Staker Count' }, beginAtZero: true },
                },
              }}
            />
          </div>

          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">Plan Popularity (Bar)</h5>
            <Bar
              data={{
                labels: stakingPlans.map((p) => formatDuration(p.durationDays)),
                datasets: [
                  {
                    label: 'Total Stakers (All Users)',
                    data: globalCounts,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                },
              }}
            />
          </div>

          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">Total Staked per Plan (Pie)</h5>
            <Pie
              data={{
                labels: stakingPlans.map((p) => formatDuration(p.durationDays)),
                datasets: [
                  {
                    data: globalTotals,
                    backgroundColor: [
                      'rgba(255, 99, 132, 0.7)',
                      'rgba(54, 162, 235, 0.7)',
                      'rgba(255, 206, 86, 0.7)',
                      'rgba(75, 192, 192, 0.7)',
                      'rgba consequencede(153, 102, 255, 0.7)',
                    ],
                    borderColor: [
                      'rgba(255, 99, 132, 1)',
                      'rgba(54, 162, 235, 1)',
                      'rgba(255, 206, 86, 1)',
                      'rgba(75, 192, 192, 1)',
                      'rgba(153, 102, 255, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                },
              }}
            />
          </div>

          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">APY Trend (Line)</h5>
            <Line
              data={{
                labels: stakingPlans.map((p) => formatDuration(p.durationDays)),
                datasets: [
                  {
                    label: 'APY %',
                    data: stakingPlans.map((p) => p.apy / 100),
                    fill: false,
                    borderColor: 'rgba(153, 102, 255, 1)',
                    tension: 0.3,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Original Staking Analytics */}
      <div className="staking-section">
        <h2 className="staking-section-title">Individual Staking Analytics</h2>
        <div className="staking-charts-grid">
          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">APY Distribution</h5>
            <Pie
              data={{
                labels: stakingPlans.map((plan) => formatDuration(plan.durationDays)),
                datasets: [
                  {
                    data: stakingPlans.map((plan) => plan.apy / 100),
                    backgroundColor: [
                      'rgba(255, 99, 132, 0.7)',
                      'rgba(54, 162, 235, 0.7)',
                      'rgba(255, 206, 86, 0.7)',
                      'rgba(75, 192, 192, 0.7)',
                      'rgba(153, 102, 255, 0.7)',
                    ],
                    borderColor: [
                      'rgba(255, 99, 132, 1)',
                      'rgba(54, 162, 235, 1)',
                      'rgba(255, 206, 86, 1)',
                      'rgba(75, 192, 192, 1)',
                      'rgba(153, 102, 255, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                },
              }}
            />
          </div>

          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">Projected Growth</h5>
            <Line
              data={{
                labels: ['Month 1', 'Month 3', 'Month 6', 'Year 1', 'Year 2', 'Year 3', 'Year 5'],
                datasets: stakingPlans.map((plan, index) => ({
                  label: formatDuration(plan.durationDays),
                  data: [
                    100,
                    index > 0 ? 100 + (plan.apy / 100) * 0.25 : 100 + (plan.apy / 100) * 0.5,
                    index > 0 ? 100 + (plan.apy / 100) * 0.5 : 100 + (plan.apy / 100) * 0.5,
                    100 + (plan.apy / 100) * 1,
                    index > 1 ? 100 + (plan.apy / 100) * 2 : null,
                    index > 2 ? 100 + (plan.apy / 100) * 3 : null,
                    index > 3 ? 100 + (plan.apy / 100) * 5 : null,
                  ].filter((val) => val !== null),
                  borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                  ][index],
                  backgroundColor: 'rgba(0,0,0,0)',
                  tension: 0.2,
                })),
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                },
              }}
            />
          </div>

          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">Locked vs Claimed</h5>
            <Bar
              data={{
                labels: stakingPlans.map((plan) => formatDuration(plan.durationDays)),
                datasets: [
                  {
                    label: 'Locked',
                    data: stakingPlans.map(
                      (plan, index) => userStakes.filter((stake) => stake.planId === index && stake.active).length
                    ),
                    backgroundColor: 'rgba(75, 192, 192, 0.7)',
                  },
                  {
                    label: 'Claimed',
                    data: stakingPlans.map(
                      (plan, index) => userStakes.filter((stake) => stake.planId === index && !stake.active).length
                    ),
                    backgroundColor: 'rgba(255, 99, 132, 0.7)',
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                },
              }}
            />
          </div>

          <div className="chart-item" style={{ height: '400px' }}>
            <h5 className="chart-title">Top Stakers</h5>
            <Bubble
              data={{
                datasets: [
                  {
                    label: 'Plan Popularity',
                    data: stakingPlans.map((plan, index) => {
                      const stakerCount = userStakes.filter((stake) => stake.planId === index).length;
                      return {
                        x: index + 1,
                        y: stakerCount,
                        r: Math.max(5, Math.min(25, stakerCount * 2)),
                      };
                    }),
                    backgroundColor: 'rgba(255, 99, 132, 0.7)',
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    title: { display: true, text: 'Staking Plan (Index)' },
                  },
                  y: {
                    title: { display: true, text: 'Number of Stakers' },
                    beginAtZero: true,
                  },
                },
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      color: '#fff',
                      boxWidth: 12,
                      padding: 10,
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const planName = stakingPlans[context.dataIndex]
                          ? formatDuration(stakingPlans[context.dataIndex].durationDays)
                          : `Plan ${context.dataIndex + 1}`;
                        return `${planName}: ${context.raw.y} Stakers`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* My Active Stakes */}
      <div className="staking-section">
        <h2 className="staking-section-title">My Active Stakes</h2>
        {userStakes.length === 0 ? (
          <div className="staking-no-stakes text-center">
            <p>You don't have any active stakes yet.</p>
            <Button variant="primary" className="connect-wallet-btn" onClick={() => window.scrollTo(0, 0)}>
              Start Staking
            </Button>
          </div>
        ) : (
          <Row>
            {userStakes
              .filter((stake) => stake.active)
              .map((stake) => {
                const plan = stakingPlans[stake.planId];
                const endTime = new Date((stake.startTime + plan.durationDays * 24 * 60 * 60) * 1000);
                const now = new Date();
                const isLocked = now < endTime;
                const progress = Math.min(
                  100,
                  ((now.getTime() - stake.startTime * 1000) / (endTime.getTime() - stake.startTime * 1000)) * 100
                );

                return (
                  <Col md={6} key={stake.id}>
                    <Card className="user-stake-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5>{formatDuration(plan.durationDays)} Plan</h5>
                          <Badge bg={isLocked ? 'warning' : 'success'}>{isLocked ? 'Locked' : 'Ready to Claim'}</Badge>
                        </div>

                        <div className="staking-stake-info">
                          <p>
                            <strong>Staked Amount:</strong> {parseFloat(formatEther(stake.amount)).toFixed(2)} MCN
                          </p>
                          <p>
                            <strong>APY:</strong> {(plan.apy / 100).toFixed(2)}%
                          </p>
                          <p>
                            <strong>End Date:</strong> {endTime.toLocaleDateString()}
                          </p>
                          <p>
                            <strong>Progress:</strong>
                          </p>
                          <ProgressBar
                            now={progress}
                            label={`${progress.toFixed(1)}%`}
                            variant={isLocked ? 'info' : 'success'}
                            className="mt-2 mb-3"
                          />
                        </div>

                        <div className="d-flex justify-content-between">
                          <Button
                            variant="success"
                            className="staking-claim-btn"
                            disabled={isLocked}
                            onClick={() => handleWithdraw(stake.id, false)}
                          >
                            Claim Rewards
                          </Button>
                          {plan.allowEarlyWithdraw && (
                            <Button
                              variant="danger"
                              className="staking-early-btn"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Early withdrawal will incur a ${(plan.penaltyPercent / 100).toFixed(2)}% penalty. Continue?`
                                  )
                                ) {
                                  handleWithdraw(stake.id, true);
                                }
                              }}
                            >
                              Early Withdraw
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
          </Row>
        )}
      </div>

      {loading && (
        <div className="staking-loading-overlay">
          <div className="staking-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default Staking;